import { useLiveQuery } from 'dexie-react-hooks';
import { Delete, ShieldCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { StaffMember } from '@sense/shared';
import { db } from '../lib/db';
import { verifyPin } from '../lib/pin';
import { Sheet } from './ui';
import { tr } from '../lib/i18n';

/**
 * A manager or the owner approves an override (price change, big discount) with their till PIN.
 * Works offline: PINs are checked against the hashes already on the device. The cashier stays signed in.
 */
export function ManagerApproval({ open, reason, onClose, onApproved }: { open: boolean; reason: string; onClose(): void; onApproved(name: string): void }) {
  const managers = useLiveQuery(() => db.staff.filter((s) => s.active && !!s.pin_hash && (s.role === 'owner' || s.role === 'manager')).toArray(), []);
  const [who, setWho] = useState<StaffMember | null>(null);
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { if (open) { setWho(managers?.length === 1 ? managers[0] : null); setPin(''); setError(null); } }, [open, managers]);

  const check = async (value: string) => {
    if (!who) return;
    if (await verifyPin(value, who.pin_hash!, who.pin_salt!)) { onApproved(who.name); return; }
    setPin(''); setError('Wrong PIN.'); navigator.vibrate?.([30, 40, 30]);
  };
  const press = (d: string) => { setError(null); const next = (pin + d).slice(0, 6); setPin(next); if (next.length === 6) void check(next); };
  useEffect(() => {
    if (!open || !who) return;
    const onKey = (e: KeyboardEvent) => {
      if (/^\d$/.test(e.key)) { e.preventDefault(); e.stopPropagation(); press(e.key); }
      else if (e.key === 'Backspace') { e.preventDefault(); setPin((p) => p.slice(0, -1)); }
      else if (e.key === 'Enter' && pin.length >= 4) { e.preventDefault(); void check(pin); }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  });

  return (
    <Sheet open={open} onClose={onClose} title={tr('Manager approval')}>
      <div className="flex flex-col items-center gap-3 pb-2">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-t-amber text-t-amber-fg"><ShieldCheck /></span>
        <p className="text-center text-sm text-muted">{reason}</p>
        {!managers?.length && <div className="rounded-2xl bg-t-red px-4 py-3 text-sm font-bold text-danger">{tr('No manager has a till PIN yet. The owner can set one in Staff.')}</div>}
        {!who ? (
          <div className="grid w-full grid-cols-2 gap-2">
            {managers?.map((m) => <button key={m.id} onClick={() => setWho(m)} className="rounded-3xl border border-line p-3 font-bold hover:border-forest-600">{m.name}<span className="block text-xs font-normal capitalize text-muted">{m.role}</span></button>)}
          </div>
        ) : (
          <>
            <div className="font-bold">{tr('{0}, enter your PIN', { 0: who.name })}</div>
            <div className="flex gap-3" role="status" aria-label={`${pin.length} digits entered`}>{Array.from({ length: Math.max(4, pin.length) }, (_, i) => <span key={i} className={`h-3.5 w-3.5 rounded-full ${i < pin.length ? 'bg-forest-700' : 'border-2 border-line'}`} />)}</div>
            <div className="h-5 text-sm font-bold text-danger" role="alert">{error}</div>
            <div className="grid w-full max-w-xs grid-cols-3 gap-2">
              {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => <button key={d} onClick={() => press(d)} className="num h-14 rounded-full bg-paper text-xl font-semibold active:bg-forest-50">{d}</button>)}
              <button aria-label="Delete digit" onClick={() => setPin((p) => p.slice(0, -1))} className="flex h-14 items-center justify-center rounded-full text-muted"><Delete /></button>
              <button onClick={() => press('0')} className="num h-14 rounded-full bg-paper text-xl font-semibold">0</button>
              <button disabled={pin.length < 4} onClick={() => void check(pin)} className="h-14 rounded-full bg-forest-700 font-bold text-white disabled:opacity-50">OK</button>
            </div>
          </>
        )}
      </div>
    </Sheet>
  );
}
